/**
 * Push Notification Dialogs
 * Broadcast, Test, and Scheduled notification dialogs
 */
import { useState } from 'react';
import { Send, MessageSquare, Calendar, RefreshCw, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

// Broadcast Dialog Component
export function BroadcastDialog({ open, onOpenChange, onSuccess, initialForm }) {
  const [form, setForm] = useState(initialForm || {
    title: '',
    body: '',
    url: '/loyalty',
    require_interaction: false
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!form.title || !form.body) {
      toast.error('Please enter both title and message');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/push/broadcast', form);
      
      if (response.data.success) {
        toast.success(`Broadcast sent to ${response.data.sent} subscribers!`);
        onOpenChange(false);
        setForm({ title: '', body: '', url: '/loyalty', require_interaction: false });
        onSuccess?.();
      } else {
        toast.error(response.data.error || 'Failed to send broadcast');
      }
    } catch (error) {
      console.error('Broadcast error:', error);
      toast.error('Failed to send broadcast');
    }
    setSending(false);
  };

  // Update form when initialForm changes (from template)
  if (initialForm && (initialForm.title !== form.title || initialForm.body !== form.body)) {
    setForm(initialForm);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Send Broadcast</DialogTitle>
          <DialogDescription>Send a notification to all active subscribers</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-foreground">Title</label>
            <Input
              placeholder="Notification title"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              className="mt-1"
              data-testid="broadcast-title"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Message</label>
            <Textarea
              placeholder="Notification message..."
              value={form.body}
              onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
              className="mt-1"
              rows={3}
              data-testid="broadcast-body"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Click URL</label>
            <Input
              placeholder="/loyalty"
              value={form.url}
              onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
              className="mt-1"
              data-testid="broadcast-url"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">Require Interaction</label>
              <p className="text-xs text-muted-foreground">Notification stays until dismissed</p>
            </div>
            <Switch
              checked={form.require_interaction}
              onCheckedChange={(checked) => setForm(f => ({ ...f, require_interaction: checked }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={sending} data-testid="send-broadcast-btn">
            {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Broadcast
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Test Notification Dialog Component
export function TestNotificationDialog({ open, onOpenChange, initialForm }) {
  const [form, setForm] = useState(initialForm || {
    customer_id: '',
    title: '',
    body: '',
    url: '/loyalty'
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!form.customer_id || !form.title || !form.body) {
      toast.error('Please fill all fields');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/push/test', form);
      
      if (response.data.success) {
        toast.success(`Test notification sent via ${response.data.delivery_method}!`);
        onOpenChange(false);
        setForm({ customer_id: '', title: '', body: '', url: '/loyalty' });
      } else if (response.data.expired) {
        toast.error('User subscription has expired');
      } else {
        toast.error(response.data.error || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('Test notification error:', error);
      toast.error('Failed to send test notification');
    }
    setSending(false);
  };

  // Update form when initialForm changes
  if (initialForm && initialForm.customer_id !== form.customer_id) {
    setForm(initialForm);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Send Test Notification</DialogTitle>
          <DialogDescription>Send a test notification to a specific user</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-foreground">Customer Email/ID</label>
            <Input
              placeholder="customer@example.com"
              value={form.customer_id}
              onChange={(e) => setForm(f => ({ ...f, customer_id: e.target.value }))}
              className="mt-1"
              data-testid="test-customer-id"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Title</label>
            <Input
              placeholder="Notification title"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              className="mt-1"
              data-testid="test-title"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Message</label>
            <Textarea
              placeholder="Notification message..."
              value={form.body}
              onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
              className="mt-1"
              rows={3}
              data-testid="test-body"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Click URL</label>
            <Input
              placeholder="/loyalty"
              value={form.url}
              onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={sending} data-testid="send-test-btn">
            {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
            Send Test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Scheduled Notification Dialog Component
export function ScheduledDialog({ 
  open, 
  onOpenChange, 
  onSuccess, 
  editingNotification,
  initialForm
}) {
  const defaultForm = {
    title: '',
    body: '',
    url: '/loyalty',
    schedule_type: 'daily',
    schedule_time: '14:00',
    schedule_days: [],
    start_date: '',
    end_date: '',
    require_interaction: false
  };

  const [form, setForm] = useState(initialForm || defaultForm);
  const [saving, setSaving] = useState(false);

  // Update form when editing or initialForm changes
  if (editingNotification && form.title !== editingNotification.title) {
    setForm({
      title: editingNotification.title,
      body: editingNotification.body,
      url: editingNotification.url || '/loyalty',
      schedule_type: editingNotification.schedule_type,
      schedule_time: editingNotification.schedule_time,
      schedule_days: editingNotification.schedule_days || [],
      start_date: editingNotification.start_date || '',
      end_date: editingNotification.end_date || '',
      require_interaction: editingNotification.require_interaction || false
    });
  }

  const handleSubmit = async () => {
    if (!form.title || !form.body || !form.schedule_time) {
      toast.error('Please fill in title, message, and schedule time');
      return;
    }

    if (form.schedule_type === 'weekly' && form.schedule_days.length === 0) {
      toast.error('Please select at least one day for weekly schedule');
      return;
    }

    setSaving(true);
    try {
      const url = editingNotification 
        ? `/push/scheduled/${editingNotification.id}`
        : `/push/scheduled`;
      
      const response = editingNotification
        ? await api.put(url, form)
        : await api.post(url, form);
      
      if (response.data.success) {
        toast.success(editingNotification ? 'Schedule updated!' : 'Notification scheduled!');
        onOpenChange(false);
        setForm(defaultForm);
        onSuccess?.();
      } else {
        toast.error(response.data.error || 'Failed to save scheduled notification');
      }
    } catch (error) {
      console.error('Scheduled notification error:', error);
      toast.error('Failed to save scheduled notification');
    }
    setSaving(false);
  };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      schedule_days: f.schedule_days.includes(day)
        ? f.schedule_days.filter(d => d !== day)
        : [...f.schedule_days, day].sort()
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {editingNotification ? 'Edit Scheduled Notification' : 'Schedule Notification'}
          </DialogTitle>
          <DialogDescription>Set up automatic notifications on a schedule</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="text-sm font-medium text-foreground">Title</label>
            <Input
              placeholder="Notification title"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              className="mt-1"
              data-testid="scheduled-title"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Message</label>
            <Textarea
              placeholder="Notification message..."
              value={form.body}
              onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
              className="mt-1"
              rows={3}
              data-testid="scheduled-body"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Click URL</label>
            <Input
              placeholder="/loyalty"
              value={form.url}
              onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
              className="mt-1"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground">Schedule Type</label>
              <Select 
                value={form.schedule_type} 
                onValueChange={(v) => setForm(f => ({ ...f, schedule_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="custom">Custom Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Time</label>
              <Input
                type="time"
                value={form.schedule_time}
                onChange={(e) => setForm(f => ({ ...f, schedule_time: e.target.value }))}
                className="mt-1"
                data-testid="scheduled-time"
              />
            </div>
          </div>

          {form.schedule_type === 'weekly' && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Select Days</label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <Button
                    key={day.value}
                    type="button"
                    variant={form.schedule_days.includes(day.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleDay(day.value)}
                    className="text-xs"
                  >
                    {day.label.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {form.schedule_type === 'custom' && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Days of Month (1-31)</label>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <Button
                    key={day}
                    type="button"
                    variant={form.schedule_days.includes(day) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleDay(day)}
                    className="w-8 h-8 p-0 text-xs"
                  >
                    {day}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {form.schedule_type === 'once' && (
            <div>
              <label className="text-sm font-medium text-foreground">Date</label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="mt-1"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground">Start Date (optional)</label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">End Date (optional)</label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">Require Interaction</label>
              <p className="text-xs text-muted-foreground">Notification stays until dismissed</p>
            </div>
            <Switch
              checked={form.require_interaction}
              onCheckedChange={(checked) => setForm(f => ({ ...f, require_interaction: checked }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-scheduled-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
            {editingNotification ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
